import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  LayoutDashboard,
  FileText,
  FolderOpen,
  Users,
  Mail,
  Settings,
  X,
  Star,
  MessageSquare,
  BarChart3,
  Award,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import DashboardStats from "../ui/dashboard-stats"
import DashboardFooter from "../ui/dashboard-footer"

const AdminSidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();

  const navigation = [
    {
      name: "Dashboard",
      href: "/admin/dashboard",
      icon: LayoutDashboard,
      badge: null,
    },
    {
      name: "Projects",
      href: "/admin/projects",
      icon: FolderOpen,
      badge: "5",
    },
    {
      name: "Blog Posts",
      href: "/admin/blogs",
      icon: FileText,
      badge: "3",
    },
    {
      name: "Certifications",
      href: "/admin/certifications",
      icon: Award,
      badge: null,
    },
    {
      name: "Users",
      href: "/admin/users",
      icon: Users,
      badge: null,
    },
    {
      name: "Testimonials",
      href: "/admin/testimonials",
      icon: MessageSquare,
      badge: "2",
    },
    {
      name: "Subscribers",
      href: "/admin/subscribers",
      icon: Mail,
      badge: "234",
    },
    {
      name: "Analytics",
      href: "/admin/analytics",
      icon: BarChart3,
      badge: null,
    },
    {
      name: "Settings",
      href: "/admin/settings",
      icon: Settings,
      badge: null,
    },
  ];

  const isActive = (href) => {
    if (href === "/admin/dashboard") {
      return (
        location.pathname === "/admin/dashboard" ||
        location.pathname === "/admin" ||
        location.pathname === "/admin/"
      );
    }
    return location.pathname.startsWith(href);
  };

  const sidebarVariants = {
    open: {
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
    closed: {
      x: -256,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
  };

  return (
    <>
      {/* Mobile sidebar */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.aside
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed inset-y-0 left-0 z-50 w-64 bg-background border-r shadow-lg lg:hidden"
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between h-16 px-4 border-b">
                <span className="text-xl font-bold">Bakhtawar Hamza</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSidebarOpen(false)}
                >
                  ✕
                </Button>
              </div>
              <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={cn(
                      "flex items-center px-2 py-2 text-sm font-medium rounded-md hover:bg-accent hover:text-accent-foreground",
                      location.pathname === item.path
                        ? "bg-accent text-accent-foreground"
                        : "text-muted-foreground"
                    )}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </Link>
                ))}
              </nav>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Desktop sidebar */}

      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 bg-background border-r">
        <div className="flex items-center justify-between h-20 px-6 border-b border-border">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center gap-2"
          >
            <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">SBF</span>
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-sm">Bakhtawar Hamza</span>
              <span className="text-xs text-muted-foreground">
                Portfolio Website
              </span>
            </div>
          </motion.div>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          ></Button>
        </div>
        <nav className="p-4 space-y-2 flex-1 ">
          <div className="space-y-1">
            {navigation.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={item.href}
                  className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive(item.href)
                      ? "bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300 shadow-sm"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="h-4 w-4 flex-shrink-0" />
                    <span>{item.name}</span>
                  </div>
                  {item.badge && (
                    <Badge
                      variant={isActive(item.href) ? "secondary" : "outline"}
                      className="text-xs h-5 px-1.5"
                    >
                      {item.badge}
                    </Badge>
                  )}
                </Link>
              </motion.div>
            ))}
          </div>
          <DashboardStats />
          <DashboardFooter />
        </nav>
      </aside>
    </>
  );
};

export default AdminSidebar;
